import apiClient from "./apiClient";
import {
  GetCustomerDto,
  GetCustomerDashboardDto,
  GetCustomerApiKeyStatsDto,
  GetApiKeyDto,
  GetApiKeyConfigDto,
  PostApiKeyConfigDto,
  GetApiKeyStatsDto,
  PostApiKeyDto,
} from "./Dtos";
import { ApiKeysPagedResult } from "./Dtos/CustomerDtos";
import { GetEmailsDto, GetEmailStatusHistory } from "./Dtos/GetEmailsDto";
import {
  GetSmsMessageDto,
  GetSmsStatusHistory,
} from "./Dtos/GetSmsMessagesDto";
import { CustomerDto } from "components/Sidebars/CustomerSidebar";

interface ApiResponse<T> {
  totalCount: number;
  items: T[];
}

export const CustomerService = {
  customer: {
    getCustomers: (
      sortBy?: string,
      pageNumber: number = 1,
      pageSize: number = 10,
      name?: string,
      phone?: string,
      email?: string,
    ) => {
      return apiClient.get<ApiResponse<GetCustomerDto>>(`api/v1/dashboard/customers`, {
        params: { sortBy,  pageNumber, pageSize, name, phone, email },
      });
    },

    getCustomer: (id: number) => {
      const fullUrl = `api/v1/dashboard/customers/${id}`;
      return apiClient.get<GetCustomerDashboardDto>(fullUrl);
    },

    getCustomerApiKeys: (
      id: number,
      page: number = 1,
      pageSize: number = 10,
      name?: string,
      email?: string,
      phone?: string,
      description?: string,
      sortBy?: string,
      sortDescending?: boolean,
    ) => {
      return apiClient.get<ApiKeysPagedResult>(
        `api/v1/dashboard/customers/${id}/apikeys`,
        { params: { page, pageSize, name, email, phone, description, sortBy, sortDescending } },
      );
    },

    exportCustomerApiKeys: (
      id: number,
      name?: string,
      email?: string,
      phone?: string,
      description?: string,
      exportFormat: string = "csv",
    ) => {
      return apiClient.get(`api/v1/dashboard/customers/${id}/apikeys`, {
        params: { name, email, phone, description, exportTo: exportFormat },
        responseType: "blob",
      });
    },

    getCustomerApiKeyStats: (customerId: number, apiKeyId: number) => {
      // const fullUrl = `api/v1/dashboard/customers/${customerId}/apikeys/${apiKeyId}/stats`;
      // return apiClient.get<GetCustomerApiKeyStatsDto>(fullUrl);

      var dummyData: GetCustomerApiKeyStatsDto = {
        sent: 100,
        received: 200,
        bounces: 300,
        complaints: 400,
        unsubscribes: 500,
      };

      return dummyData;
    },

    exportCustomers: (
      sortBy?: string,
      name?: string,
      phone?: string,
      email?: string,
      exportFormat: string = "csv",
    ) => {
      return apiClient.get(`api/v1/dashboard/customers`, {
        params: { sortBy, name, phone, email, exportTo: exportFormat },
        responseType: "blob",
      });
    },

    createCustomer: (customer: CustomerDto) => {
      return apiClient.post<GetCustomerDto>("api/v1/dashboard/customers", customer);
    },

    updateCustomer: (customerId: number, customer: CustomerDto) => {
      return apiClient.put<GetCustomerDto>(
        `api/v1/dashboard/customers/${customerId}`,
        customer,
      );
    },
  },

  email: {
    getCustomerEmailsByApiKeyId: (
      apiKeyId: number,
      sortBy?: string,
      sortDescending: boolean = false,
      pageNumber: number = 1,
      pageSize: number = 10,
      recipientEmails?: string,
      senderEmails?: string,
      subject?: string,
      status?: string,
      createdFrom?: string,
      createdTo?: string,
    ) => {
      return apiClient.get<ApiResponse<GetEmailsDto>>(
        `api/v1/request-analytics/list/email-messages`,
        {
          params: { apiKeyId, sortBy, sortDescending, pageNumber, pageSize, recipientEmails, senderEmails, subject, status, createdFrom, createdTo },
        },
      );
    },

    exportEmailMessagesByApiKeyId: (
      apiKeyId: number,
      sortBy?: string,
      sortDescending?: boolean,
      recipientEmails?: string,
      senderEmails?: string,
      subject?: string,
      status?: string,
      createdFrom?: string,
      createdTo?: string,
      exportFormat: string = "csv",
    ) => {
      return apiClient.get(
        `api/v1/request-analytics/list/email-messages`,
        { params: { apiKeyId, exportTo: exportFormat, sortBy, sortDescending, recipientEmails, senderEmails, subject, status, createdFrom, createdTo }, responseType: "blob" },
      );
    },

    getCustomerEmailsByCustomerId: (
      customerId: number,
      sortBy?: string,
      sortDescending: boolean = false,
      pageNumber: number = 1,
      pageSize: number = 10,
      recipientEmails?: string,
      senderEmails?: string,
      subject?: string,
      status?: string,
      createdFrom?: string,
      createdTo?: string,
    ) => {
      return apiClient.get<ApiResponse<GetEmailsDto>>(
        `api/v1/request-analytics/list/email-messages`,
        {
          params: { customerId, sortBy, sortDescending, pageNumber, pageSize, recipientEmails, senderEmails, subject, status, createdFrom, createdTo },
        },
      );
    },

    exportEmailMessagesByCustomerId: (
      customerId: number,
      sortBy?: string,
      sortDescending?: boolean,
      recipientEmails?: string,
      senderEmails?: string,
      subject?: string,
      status?: string,
      createdFrom?: string,
      createdTo?: string,
      exportFormat: string = "csv",
    ) => {
      return apiClient.get(
        `api/v1/request-analytics/list/email-messages`,
        { params: { customerId, exportTo: exportFormat, sortBy, sortDescending, recipientEmails, senderEmails, subject, status, createdFrom, createdTo }, responseType: "blob" },
      );
    },

    getEmailHistoryByEmailId: (emailId: number, apiKey: string) => {
      return apiClient.get<GetEmailStatusHistory[]>(
        `api/v1/emails/${emailId}/history`,
        {
          headers: {
            "X-API-Key": apiKey,
          },
        },
      );
    },

    updateBlockStatus: (
      data: {
        email: string;
        shouldBlock: boolean;
        reason: string;
      },
      apiKey: string,
    ) => {
      return apiClient.put(`api/v1/emails/update-block-status`, data, {
        headers: {
          "Content-Type": "application/json-patch+json",
          "X-API-Key": apiKey,
        },
      });
    },

    // chaned api endpoint
    // Backend: POST  requires X-API-Key
    // To should be string[] (array of recipients); for a single recipient wrap in []
    sendEmail: async (
      data: {
        To: string[];
        Subject: string;
        HtmlContent: string;
        EmailTemplateId?: number;
      },
      apiKey: string,
    ) => {
      return await apiClient.post<{ data: any }>(`api/v1/emails/send-bulk`, data, {
        headers: {
          "Content-Type": "application/json-patch+json",
          "X-API-Key": apiKey,
        },
      });
    },
  },

  sms: {
    // changed api endpoint and response shape
    // Backend: POST  requires X-API-Key
    // To should be string[] (array of recipients); Message replaces Body
    sendSms: async (
      data: {
        To: string[];
        Message: string;
      },
      apiKey: string,
    ) => {
      return await apiClient.post<{ data: any }>(`api/v1/sms/send-bulk`, data, {
        headers: {
          "Content-Type": "application/json-patch+json",
          "X-API-Key": apiKey,
        },
      });
    },

    exportSmsMessagesByApiKeyId: (
      apiKeyId: number,
      sortBy?: string,
      sortDescending?: boolean,
      recipientNumbers?: string,
      senderNumbers?: string,
      status?: string,
      messageBody?: string,
      dateFrom?: string,
      dateTo?: string,
      exportFormat: string = "csv",
    ) => {
      return apiClient.get(
        `api/v1/request-analytics/list/sms-messages`,
        { params: { apiKeyId, exportTo: exportFormat, sortBy, sortDescending, recipientNumbers, senderNumbers, status, messageBody, dateFrom, dateTo }, responseType: "blob" },
      );
    },

    getCustomerSmsMessagesByCustomerId: (
      customerId: number,
      sortBy?: string,
      sortDescending: boolean = false,
      pageNumber: number = 1,
      pageSize: number = 10,
      recipientNumbers?: string,
      senderNumbers?: string,
      status?: string,
      messageBody?: string,
      dateFrom?: string,
      dateTo?: string,
    ) => {
      return apiClient.get<ApiResponse<GetSmsMessageDto>>(
        `api/v1/request-analytics/list/sms-messages`,
        {
          params: { customerId, sortBy, sortDescending, pageNumber, pageSize, recipientNumbers, senderNumbers, status, messageBody, dateFrom, dateTo },
        },
      );
    },

    exportSmsMessagesByCustomerId: (
      customerId: number,
      sortBy?: string,
      sortDescending?: boolean,
      recipientNumbers?: string,
      senderNumbers?: string,
      status?: string,
      messageBody?: string,
      dateFrom?: string,
      dateTo?: string,
      exportFormat: string = "csv",
    ) => {
      return apiClient.get(
        `api/v1/request-analytics/list/sms-messages`,
        { params: { customerId, exportTo: exportFormat, sortBy, sortDescending, recipientNumbers, senderNumbers, status, messageBody, dateFrom, dateTo }, responseType: "blob" },
      );
    },

    getCustomerSmsMessagesByApiKeyId: (
      apiKeyId: number,
      sortBy?: string,
      sortDescending: boolean = false,
      pageNumber: number = 1,
      pageSize: number = 10,
      recipientNumbers?: string,
      senderNumbers?: string,
      status?: string,
      messageBody?: string,
      dateFrom?: string,
      dateTo?: string,
    ) => {
      return apiClient.get<ApiResponse<GetSmsMessageDto>>(
        `api/v1/request-analytics/list/sms-messages`,
        {
          params: { apiKeyId, sortBy, sortDescending, pageNumber, pageSize, recipientNumbers, senderNumbers, status, messageBody, dateFrom, dateTo },
        },
      );
    },

    // changed api endpoint
    // Backend: GET requires X-API-Key
    getSmsHistoryBySmsId: (smsId: number, apiKey: string) => {
      return apiClient.get<GetSmsStatusHistory[]>(`api/v1/sms/${smsId}/history`, {
        headers: {
          "X-API-Key": apiKey,
        },
      });
    },

    // changed api endpoint
    updateBlockStatus: (
      data: {
        phoneNumber: string;
        shouldBlock: boolean;
        reason: string;
      },
      apiKey: string,
    ) => {
      return apiClient.put(`api/v1/sms/update-block-status`, data, {
        headers: {
          "Content-Type": "application/json-patch+json",
          "X-API-Key": apiKey,
        },
      });
    },
  },

  apiKey: {
    getApiKeyConfigs: (customerId: number, apiKeyId: number) => {
      return apiClient.get<GetApiKeyConfigDto[]>(
        `api/v1/dashboard/customers/${customerId}/apikeys/${apiKeyId}/configs`,
      );
    },
    updateApiKeyConfigs: (
      customerId: number,
      apiKeyId: number,
      configs: PostApiKeyConfigDto[],
    ) => {
      return apiClient.post<GetApiKeyConfigDto[]>(
        `api/v1/dashboard/customers/${customerId}/apikeys/${apiKeyId}/configs`,
        configs,
      );
    },

    getApiKeyById: (apiKeyId: number) => {
      const fullUrl = `api/v1/dashboard/apikeys/${apiKeyId}`;
      return apiClient.get<GetApiKeyDto>(fullUrl);
    },

    // Resolves the raw key value sent as X-API-Key to the emails/sms endpoints.
    getApiKeyValue: async (apiKeyId: number) => {
      const response = await apiClient.get<GetApiKeyDto>(
        `api/v1/dashboard/apikeys/${apiKeyId}`,
      );
      return response.data.key;
    },

    createApiKey: (apiKey: PostApiKeyDto) => {
      return apiClient.post<GetApiKeyDto>("api/v1/dashboard/apikeys", apiKey);
    },

    // NOTE: Backend expects PutApiKeyDto, not PostApiKeyDto — consider a separate type
    updateApiKey: (apiKeyId: number, apiKey: PostApiKeyDto) => {
      return apiClient.put<GetApiKeyDto>(
        `api/v1/dashboard/apikeys/${apiKeyId}`,
        apiKey,
      );
    },
  },
};
